import { useState, useEffect } from 'react';
import { getPosition } from '../utils/devicePosition';

const CACHE_KEY = 'golf_weather_v1';
const CACHE_TTL = 30 * 60 * 1000;

function degToCardinal(deg) {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(deg / 45) % 8];
}

function codeToEmoji(code) {
  if (code === 0) return '☀️';
  if (code <= 3)  return '⛅';
  if (code <= 48) return '🌫️';
  if (code <= 55) return '🌦️';
  if (code <= 65) return '🌧️';
  if (code <= 75) return '❄️';
  if (code <= 82) return '🌧️';
  return '⛈️';
}

async function fetchWeather({ lat, lng: lon }) {
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast` +
      `?latitude=${lat.toFixed(4)}&longitude=${lon.toFixed(4)}` +
      `&current=temperature_2m,wind_speed_10m,wind_direction_10m,precipitation_probability,weather_code` +
      `&wind_speed_unit=mph&timezone=auto`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();
    const c = json.current;
    const data = {
      temp:       Math.round(c.temperature_2m),
      windSpeed:  Math.round(c.wind_speed_10m),
      windDir:    degToCardinal(c.wind_direction_10m),
      rainChance: c.precipitation_probability ?? 0,
      emoji:      codeToEmoji(c.weather_code),
    };
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
    return data;
  } catch {
    return null;
  }
}

/**
 * Home's weather — and Home is where the app asks for location: on every open,
 * as it always has, whenever the weather isn't already cached. The fix it gets
 * is held (utils/devicePosition), so the course pickers that follow use it
 * without asking again.
 */
function readCachedWeather() {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;
  } catch {}
  return null;
}

export function useWeather() {
  // Read on the FIRST render, not in an effect: from an effect, cached weather
  // arrived one render after the page, so the strip popped in a frame late on
  // every open even though the answer was already on the device.
  const [weather, setWeather] = useState(readCachedWeather);

  useEffect(() => {
    let live = true;
    if (readCachedWeather()) {
      // Warm the fix for the course pickers where it is already allowed.
      getPosition();
      return undefined;
    }

    (async () => {
      const position = await getPosition({ prompt: true });
      if (!position || !live) return;
      const data = await fetchWeather(position);
      if (data && live) setWeather(data);
    })();
    return () => { live = false; };
  }, []);

  return weather;
}
